import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getRisks } from '../../api/riskApi';
import { getRiskLevelLabel, getRiskLevelClass, STRENGTH_OPTIONS, FREQUENCY_OPTIONS } from '../../utils/riskUtils';
import './risk.css';

const valColor = (v) => { const n = Number(v); if (n >= 3) return 'danger'; if (n === 2) return 'warning'; return 'success'; };

function FreqBadge({ value }) {
  if (value === null || value === undefined || value === '') return <span className="text-muted">-</span>;
  const label = FREQUENCY_OPTIONS.find(o => String(o.value) === String(value))?.label?.split(' ')[0] || '-';
  return <span className={`badge bg-${valColor(value)}`}>{value} - {label}</span>;
}

function StrBadge({ value }) {
  if (value === null || value === undefined || value === '') return <span className="text-muted">-</span>;
  const label = STRENGTH_OPTIONS.find(o => String(o.value) === String(value))?.label?.split(' ')[0] || '-';
  return <span className={`badge bg-${valColor(value)}`}>{value} - {label}</span>;
}

function LevelBadge({ value }) {
  if (value === null || value === undefined || value === '') return <span className="text-muted">-</span>;
  return <span className={`badge bg-${getRiskLevelClass(value)}`}>{value} - {getRiskLevelLabel(value)}</span>;
}

function parseChargeList(raw) {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch { return []; }
}

function chargeDisplay(raw) {
  const list = parseChargeList(raw);
  if (list.length === 0) return '-';
  const first = list[0].name || '-';
  return list.length > 1 ? `${first} 외 ${list.length - 1}` : first;
}

function RiskList() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchRisks = useCallback(async () => {
    try {
      const data = await getRisks(token);
      setRisks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('유해위험 목록 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRisks();
  }, [fetchRisks]);


  const filtered = risks.filter(r =>
    (r.risk_category_num || '').includes(searchTerm) ||
    (r.risk_category_context || '').includes(searchTerm) ||
    (r.risk_factors || '').includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="text-center text-muted mt-5">
        <div className="spinner-border text-secondary mb-3" role="status" />
        <p>불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="risk-header">
            <h4>위험성 평가 목록</h4>
            <div className="risk-header-actions">
              <button className="btn btn-primary" onClick={() => navigate('/risk/write')}>
                새 항목 등록
              </button>
            </div>
          </div>

          <div className="risk-search-area">
            <input
              type="text"
              className="form-control"
              placeholder="분류넘버, 분류내용, 유해위험 요인 검색"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-outline-danger" onClick={() => setSearchTerm('')}>
              초기화
            </button>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center text-muted mt-5">
              <p>등록된 항목이 없습니다.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-nowrap align-middle table-hover mb-0 risk-list-table">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 90 }}>위험분류</th>
                    <th>유해위험요인파악</th>
                    <th className="text-center" style={{ width: 160 }}>
                      <div>현재 위험성</div>
                      <div className="d-flex justify-content-around mt-1" style={{ fontSize: '0.75rem', fontWeight: 'normal', color: '#6c757d' }}>
                        <span>빈도</span><span>강도</span><span>위험성</span>
                      </div>
                    </th>
                    <th>개선대책</th>
                    <th className="text-center" style={{ width: 95 }}>예정일</th>
                    <th className="text-center" style={{ width: 95 }}>완료일</th>
                    <th className="text-center" style={{ width: 110 }}>담당자</th>
                    <th className="text-center" style={{ width: 160 }}>
                      <div>개선 후 위험성</div>
                      <div className="d-flex justify-content-around mt-1" style={{ fontSize: '0.75rem', fontWeight: 'normal', color: '#6c757d' }}>
                        <span>빈도</span><span>강도</span><span>위험성</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/risk/${r.id}`)}>
                      <td>{r.risk_category_num || '-'}</td>
                      <td className="risk-truncate" style={{ whiteSpace: 'pre-wrap' }}>{r.risk_factors || '-'}</td>
                      <td className="text-center">
                        <div className="d-flex justify-content-around align-items-center">
                          <FreqBadge value={r.current_risk_frequency} />
                          <StrBadge value={r.current_risk_strength} />
                          <LevelBadge value={r.current_risk_level} />
                        </div>
                      </td>
                      <td className="risk-truncate" style={{ whiteSpace: 'pre-wrap' }} >{r.risk_reduction_measures || '-'}</td>
                      <td className="text-center">{r.request_date || '-'}</td>
                      <td className="text-center">{r.complete_date || '-'}</td>
                      <td className="text-center">{chargeDisplay(r.charge_info)}</td>
                      <td className="text-center">
                        <div className="d-flex justify-content-around align-items-center">
                          <FreqBadge value={r.after_risk_frequency} />
                          <StrBadge value={r.after_risk_strength} />
                          <LevelBadge value={r.after_risk_level} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RiskList;
